import cn from 'classnames';
import { Currency } from '@round/api';
import { MoneyValue, Skeleton, Tooltip } from '@round/creatorbase-ui';
import { usePreviewPostCost } from '@round/creatorbase-helpers/api-hooks';
import { asMoneyWithoutZeros } from 'helpers';
import { NO_VALUE } from '../../../../../../helpers';
import styles from './TotalCostCell.module.css';

const PREVIEW_ERROR = 'Something went wrong fetching estimated fees';

type Props = {
    campaignId: number;
    /** What the endpoint converts the total into. */
    campaignCurrency: Currency | undefined;
    cost: MoneyValue;
};

/** What the post costs the client once fees are applied — previewed via the same endpoint a single-post add uses. */
export const TotalCostCell = ({ campaignId, campaignCurrency, cost }: Props) => {
    const {
        data: preview,
        isLoading,
        error,
    } = usePreviewPostCost({
        campaign_id: campaignId,
        base_cost: cost.value ? Number(cost.value) : undefined,
        currency_id: cost.currency?.value,
    });

    // isLoading is only true before any data has loaded, so a keystroke re-fetch keeps the last total shown.
    if (isLoading) {
        return <Skeleton className={styles.skeleton} />;
    }

    // Nothing to price until a cost is typed in — checked before preview, which can still hold the
    // last fetch's answer via keepPreviousData even after the cost behind it was cleared.
    if (!cost.value || (!preview && !error)) {
        return <span className={styles.empty}>{NO_VALUE}</span>;
    }

    const total = preview ? (
        <span className={cn(styles.total, { [styles.error]: !!error })}>
            <span>{asMoneyWithoutZeros(preview.cost, campaignCurrency)}</span>
            {campaignCurrency?.name ? <span className={styles.currencyName}>{campaignCurrency.name}</span> : null}
        </span>
    ) : (
        <span className={cn(styles.empty, styles.error)}>{NO_VALUE}</span>
    );

    if (!error) {
        return total;
    }

    // A refused preview leaves the last total standing (or NO_VALUE if there wasn't one), so say why.
    return (
        <Tooltip>
            <Tooltip.Trigger asChild>{total}</Tooltip.Trigger>
            <Tooltip.Content>{PREVIEW_ERROR}</Tooltip.Content>
        </Tooltip>
    );
};
